/**
 * Shared design tokens for the analytics components.
 * One palette + one tooltip + one empty state, reused everywhere,
 * so charts, cards, and KPIs read as one system instead of five.
 */

export const palette = {
  primary: "#4B3FAE", // primary-500
  series: [
    "#4B3FAE", // primary
    "#06B6D4", // cyan
    "#10B981", // emerald
    "#F59E0B", // amber
    "#F43F5E", // rose
    "#8B5CF6", // violet
    "#64748B", // slate
  ],
  grid: "#E2E2EA", // neutral-200
  axis: "#A5A5B8", // neutral-400
  axisStrong: "#7C7C93", // neutral-500
  border: "#E2E2EA",
};

export const toneColor = {
  default: {
    text: "text-neutral-900 dark:text-neutral-100",
    bg: "bg-neutral-50 dark:bg-neutral-800/60",
    dot: "#334155",
  },
  success: {
    text: "text-emerald-600 dark:text-emerald-400",
    bg: "bg-emerald-50 dark:bg-emerald-500/10",
    dot: "#10B981",
  },
  warning: {
    text: "text-amber-600 dark:text-amber-400",
    bg: "bg-amber-50 dark:bg-amber-500/10",
    dot: "#F59E0B",
  },
  danger: {
    text: "text-rose-600 dark:text-rose-400",
    bg: "bg-rose-50 dark:bg-rose-500/10",
    dot: "#F43F5E",
  },
  muted: {
    text: "text-neutral-400 dark:text-neutral-500",
    bg: "bg-neutral-50 dark:bg-neutral-800/40",
    dot: "#94A3B8",
  },
};

export const tickStyle = { fontSize: 11, fill: palette.axis, fontWeight: 500 };
export const tickStyleStrong = {
  fontSize: 11,
  fill: palette.axisStrong,
  fontWeight: 500,
};

/**
 * Compact floating tooltip shared by Bar/Line/Donut.
 * label + a single accent dot + tabular value keeps every chart's
 * hover state visually identical.
 */
export const ChartTooltip = ({ active, payload, label, formatter }) => {
  if (!active || !payload?.length) return null;
  const entry = payload[0];
  const value = formatter ? formatter(entry.value, entry) : entry.value;

  return (
    <div className="flex items-center gap-2 rounded-lg border border-neutral-200 dark:border-neutral-700 bg-white/95 dark:bg-neutral-900/95 px-2.5 py-1.5 text-xs shadow-lg shadow-neutral-900/6 dark:shadow-black/30 backdrop-blur-sm">
      <span
        className="h-2 w-2 shrink-0 rounded-full"
        style={{
          backgroundColor:
            entry.color || entry.payload?.fill || palette.primary,
        }}
      />
      {label != null && (
        <span className="font-medium text-neutral-500 dark:text-neutral-400">
          {label}
        </span>
      )}
      <span className="font-semibold tabular-nums text-neutral-900 dark:text-neutral-100">
        {value}
      </span>
    </div>
  );
};

/**
 * Consistent "no data" state — replaces the plain centered text
 * each chart used to implement separately.
 */
export const ChartEmpty = ({ height }) => (
  <div
    className="flex flex-col items-center justify-center gap-1.5 text-neutral-300 dark:text-neutral-600"
    style={{ height }}
  >
    <svg
      width="26"
      height="26"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M3 3v18h18M8 17V10m4 7V6m4 11v-4"
      />
    </svg>
    <span className="text-xs font-medium text-neutral-400 dark:text-neutral-500">
      No data yet
    </span>
  </div>
);
